<template>
  <Modal
    :model-value="showModal"
    :close-on-click-outside="false"
    @update:model-value="$emit('update:showModal', $event)"
  >
    <div class="mb-6 flex justify-end">
      <CVButton variant="link" @click="$emit('update:showModal', false)">
        <XIcon class="h-4 w-4" />
        {{ t('close') }}
      </CVButton>
    </div>
    <div v-if="showModal && car">
      <OverviewAndExtras
        v-if="activePage === Page.OVERVIEW_AND_EXTRAS"
        :car="car"
        :create-booking-props="createBookingProps"
        :is-agency-booking="isAgencyBooking"
        :is-loading="isCreatingPricing"
        @update-booked-extras="bookedExtras = $event"
        @update-custom-booked-extras="customBookedExtras = $event"
        @update-is-agency-booking="isAgencyBooking = $event"
        @continue="continueFromOverviewAndExtras"
      />
      <AddAgency
        v-if="activePage === Page.ADD_AGENCY"
        v-model:agency="agency"
        :total-price="pricing!.totalPrice"
        :is-long-term="!!longTerm"
        @continue="activePage = Page.CUSTOMER_DATA"
        @back="activePage = Page.OVERVIEW_AND_EXTRAS"
      />
      <CustomerData
        v-if="activePage === Page.CUSTOMER_DATA"
        v-model:customer-data="customerData"
        :agency="agency"
        :tax-procedure="taxProcedure"
        :create-booking-props="createBookingProps"
        :car="car"
        :is-loading="isLoading"
        @update:tax-procedure="handleTaxProcedureUpdate"
        @update:tax-inputs="updateTaxProcedureInputs"
        @apply-tax-procedure-suggestion="applyTaxProcedureSuggestion"
        @back="backFromAddCustomer"
        @continue="createCustomerAndBooking"
        @update-agency="handleUpdateAgency"
      />
    </div>
    <div v-else class="flex justify-center py-12">
      <Spinner />
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import { computed, ref, watch } from 'vue';
import OverviewAndExtras from './modal-forms/overview-and-extras/OverviewAndExtras.vue';
import { useCar } from '@/queries/use-cars';
import { AdditionalDriver, type CreateBookingDto } from '@/entities/bookings/booking.entity';
import AddAgency from './modal-forms/add-agency/AddAgency.vue';
import type { CreateCustomerUserDto, User } from '@/entities/auth/user.entity';
import type { CustomerDataDto } from '@/entities/bookings/customer-data.dto';
import CustomerData from './modal-forms/customer-data/CustomerData.vue';
import { Gender } from '@/entities/auth/gender.enum';
import { useCreateUserOrReturnId } from '@/queries/use-users';
import { useCreateBooking } from '@/queries/use-bookings';
import { useI18n } from 'vue-i18n';
import { Local } from '@/entities/auth/local.enum';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { useCreatePricingForBookingProcess } from '@/views/bookings/booking-create/components/use-create-pricing-for-booking-process';
import { Alert } from '@/services/alert.service';
import router from '@/router';
import { XIcon } from 'lucide-vue-next';
import {
  TaxProcedure,
  type BookingPricingDto,
  type UpdateCustomBookedExtraDto,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import type { BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import { useSuggestedTaxProcedure } from '@/hooks/use-suggested-tax-procedure';
import { isLongTermPricing } from '@/utils/pricing.utils';
import { isCustomerPaidPartner } from '@/utils/long-term-create.utils';
import { bookingLocationToCreateFields } from '@/utils/booking-location-to-location-dto';

enum Page {
  OVERVIEW_AND_EXTRAS = 'OVERVIEW_AND_EXTRAS',
  ADD_AGENCY = 'ADD_AGENCY',
  CUSTOMER_DATA = 'CUSTOMER_DATA',
}

const props = defineProps<{
  showModal: boolean;
  createBookingProps: CreateBookingProps;
}>();

const {
  selectedOffer,
  startDate,
  endDate,
  pickupLocation,
  dropoffLocation,
  pricing,
  insuranceCase,
  partner,
  longTerm,
} = props.createBookingProps;

const emit = defineEmits<{
  (e: 'update:showModal', value: boolean): void;
}>();

const { t } = useI18n();

const activePage = ref(Page.OVERVIEW_AND_EXTRAS);
const bookedExtras = ref<BookedExtraSelection[]>([]);
const customBookedExtras = ref<UpdateCustomBookedExtraDto[] | undefined>(undefined);
const isAgencyBooking = ref(partner.value ? true : false);
const agency = ref<User | null>(partner.value ?? null);
const customerData = ref<CustomerDataDto & { email?: string | null }>({
  preferredLocal: Local.EN_US,
});
const taxProcedureInputs = ref<{
  vatId?: string | null;
  country?: string | null;
}>({});

const {
  taxProcedure,
  setTaxProcedure,
  applySuggestion: applyTaxProcedureSuggestion,
} = useSuggestedTaxProcedure({
  mode: 'create',
  startDate: () => startDate.value,
  endDate: () => endDate.value,
  vatId: () => taxProcedureInputs.value.vatId ?? customerData.value.vatId,
  country: () => taxProcedureInputs.value.country ?? customerData.value.country,
});

const handleTaxProcedureUpdate = (value: TaxProcedure) => {
  setTaxProcedure(value, true);
};

const updateTaxProcedureInputs = (inputs: { vatId?: string | null; country?: string | null }) => {
  taxProcedureInputs.value = inputs;
};

const { data: car } = useCar(computed(() => selectedOffer.value?.car.id));
const { mutateAsync: createPricing, isPending: isCreatingPricing } =
  useCreatePricingForBookingProcess(props.createBookingProps);

const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateUserOrReturnId();
const { mutateAsync: createBooking, isPending: isCreatingBooking } = useCreateBooking();

const isLoading = computed(
  () => isCreatingUser.value || isCreatingBooking.value || isCreatingPricing.value,
);

const refreshPricing = async () => {
  if (!selectedOffer.value || !pickupLocation.value || !dropoffLocation.value) {
    return;
  }
  pricing.value =
    (await createPricing({
      bookedExtras: bookedExtras.value,
      agency: agency.value,
      customBookedExtras: customBookedExtras.value,
      taxProcedure: taxProcedure.value,
    })) ?? pricing.value;
};

watch(
  () => props.showModal,
  (showModal) => {
    if (!showModal) return;
    activePage.value = Page.OVERVIEW_AND_EXTRAS;
    bookedExtras.value = [];
    customBookedExtras.value = undefined;
    pricing.value = selectedOffer.value!.pricing;
    agency.value = partner.value ?? null;
    isAgencyBooking.value = partner.value ? true : false;
    customerData.value = { preferredLocal: Local.EN_US, email: null };
    taxProcedureInputs.value = {};
    applyTaxProcedureSuggestion();
    if (insuranceCase.value) {
      presetCustomerData({
        ...insuranceCase.value.customerData,
        email: insuranceCase.value.customer?.email,
      });
    }
  },
  { immediate: true },
);

watch([agency, bookedExtras, customBookedExtras, taxProcedure], refreshPricing, { deep: true });

function presetCustomerData(
  initialCustomerData: (CustomerDataDto & { email?: string | null }) | User | null,
) {
  customerData.value = initialCustomerData
    ? {
        firstName: initialCustomerData.firstName,
        lastName: initialCustomerData.lastName,
        company: initialCustomerData.company ?? undefined,
        gender: initialCustomerData.gender,
        dateOfBirth: initialCustomerData.dateOfBirth ?? undefined,
        phone: initialCustomerData.phone ?? undefined,
        street: initialCustomerData.street ?? undefined,
        city: initialCustomerData.city ?? undefined,
        zip: initialCustomerData.zip ?? undefined,
        country: initialCustomerData.country ?? undefined,
        vatId: initialCustomerData.vatId ?? undefined,
        preferredLocal: initialCustomerData.preferredLocal,
        email: initialCustomerData.email ?? null,
      }
    : { preferredLocal: Local.EN_US };
  taxProcedureInputs.value = {
    vatId: customerData.value.vatId,
    country: customerData.value.country,
  };
}

const continueFromOverviewAndExtras = async () => {
  if (!pricing.value || !('id' in pricing.value && pricing.value.id)) {
    await refreshPricing();
  }
  if (isAgencyBooking.value) {
    activePage.value = Page.ADD_AGENCY;
  } else {
    activePage.value = Page.CUSTOMER_DATA;
    agency.value = null;
  }
};

const backFromAddCustomer = () => {
  if (isAgencyBooking.value) {
    activePage.value = Page.ADD_AGENCY;
  } else {
    activePage.value = Page.OVERVIEW_AND_EXTRAS;
  }
};

const createCustomerAndBooking = async () => {
  if (!pricing.value || !pickupLocation.value || !dropoffLocation.value || !selectedOffer.value)
    return;

  let customerId: string | null = null;
  if (customerData.value.email) {
    const createUserDto: CreateCustomerUserDto = {
      ...customerData.value,
      firstName: customerData.value.firstName ?? '',
      lastName: customerData.value.lastName ?? '',
      gender: customerData.value.gender ?? Gender.MALE,
      preferredLocal: customerData.value.preferredLocal ?? Local.EN_US,
      email: customerData.value.email!,
    };
    customerId = (await createUser(createUserDto)).id;
  }

  // Get additional drivers count from booked extras
  const additionalDriversExtra = pricing.value?.availableExtras?.find(
    (extra) => extra.type === VehicleExtraType.ADDITIONAL_DRIVER,
  );
  const bookedAdditionalDriverExtra = bookedExtras.value.find(
    (be) => be.vehicleExtraId === additionalDriversExtra?.id,
  );
  const additionalDriversCount = Number(
    additionalDriversExtra?.options.find(
      (option) => option.id === bookedAdditionalDriverExtra?.vehicleExtraOptionId,
    )?.value ?? 0,
  );
  const additionalDrivers = isLongTermPricing(pricing.value)
    ? []
    : Array.from({ length: additionalDriversCount }, (_, i) => i + 1).map(
        () => new AdditionalDriver(),
      );

  const { email: _omitEmail, ...customerDataWithoutEmail } = customerData.value;
  const createBookingDto: CreateBookingDto = {
    startDate: startDate.value,
    endDate: endDate.value,
    bookedCarId: selectedOffer.value.car.id,
    ...bookingLocationToCreateFields('pickup', pickupLocation.value),
    ...bookingLocationToCreateFields('dropoff', dropoffLocation.value),
    agencyId: agency.value?.id,
    pricing: {
      acceptedPrice: pricing.value!.totalPrice,
      bookedExtras: bookedExtras.value,
      customBookedExtras: customBookedExtras.value,
      ...(longTerm.value ? { type: 'LONG_TERM' as const } : {}),
    } as BookingPricingDto,
    customerId,
    customerData: customerDataWithoutEmail,
    taxProcedure: taxProcedure.value,
    additionalDrivers: additionalDrivers,
    insuranceCaseId: insuranceCase.value?.id,
    status: insuranceCase.value ? BookingStatus.CONFIRMED : BookingStatus.OPEN,
  };

  const { id, bookingNumber } = await createBooking(createBookingDto);

  emit('update:showModal', false);

  const { isConfirmed } = await Alert.fire({
    titleText: t('bookingCreated'),
    text: t('bookingCreatedText', { bookingNumber }),
    icon: 'success',
    showCloseButton: true,
    confirmButtonText: t('viewBooking'),
  });

  if (isConfirmed) {
    await router.push({ name: 'bookingEdit', params: { id } });
  }
};

const handleUpdateAgency = (value?: User | null) => {
  if (longTerm.value && value && !isCustomerPaidPartner(value)) {
    void Alert.fire({
      text: t('longTermCustomerPaidAgency'),
      icon: 'error',
    });
    return;
  }
  agency.value = value ?? null;
  isAgencyBooking.value = value ? true : false;
};
</script>

<i18n lang="json">
{
  "en": {
    "bookingCreated": "Booking created",
    "bookingCreatedText": "The booking number is {bookingNumber}.",
    "viewBooking": "View Booking",
    "close": "Close",
    "longTermCustomerPaidAgency": "Long-term rental can only be booked with a customer-paid agency."
  },
  "de": {
    "bookingCreated": "Buchung erstellt",
    "bookingCreatedText": "Die Buchungsnummer lautet {bookingNumber}.",
    "viewBooking": "Buchung ansehen",
    "close": "Schließen",
    "longTermCustomerPaidAgency": "Langzeitmiete kann nur mit einer kundenseitig abgerechneten Agency gebucht werden."
  },
  "es": {
    "bookingCreated": "Reserva creada",
    "bookingCreatedText": "El número de reserva es {bookingNumber}.",
    "viewBooking": "Ver reserva",
    "close": "Cerrar",
    "longTermCustomerPaidAgency": "El alquiler a largo plazo solo se puede reservar con una agencia de cobro al cliente."
  }
}
</i18n>
